"use client"

import { Button } from "@/components/ui/button"
import { Archive, ArrowLeft, Battery, ChevronLeft, Folder, Search, Signal, SquarePen, Wifi } from "lucide-react"
import Image from "next/image"
import { useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useState } from "react"
import "./styles.css"

function IPhoneEmailProductFinderContent() {
  const searchParams = useSearchParams()
  const isAutoMode = searchParams.get('auto') === '1'
  const [isEmailClicked, setIsEmailClicked] = useState(false)
  const [showCursor, setShowCursor] = useState(false)

  // Product Finder State
  const [step, setStep] = useState(1)
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null)
  const [selectedGender, setSelectedGender] = useState<string | null>(null)
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null)

  useEffect(() => {
    // Only start the cursor animation sequence in auto mode
    if (isAutoMode) {
      // Show cursor and start its animation
      setShowCursor(true);

      // Trigger the slide right after cursor animation completes
      const timer = setTimeout(() => {
        setIsEmailClicked(true);
      }, 4500); // Match the cursor animation duration

      return () => clearTimeout(timer);
    }
  }, [isAutoMode]);

  const targetOptions = [
    { id: "myself", label: "For myself" },
    { id: "friend", label: "For a friend" },
    { id: "loved-one", label: "For a loved one" },
    { id: "family", label: "For a family member" },
  ]

  const genderOptions = [
    { id: "man", label: "A man", image: "/iframe/product-finder/new/gender/male.png" },
    { id: "woman", label: "A woman", image: "/iframe/product-finder/new/gender/female.png" },
    { id: "diverse", label: "Diverse", image: "/iframe/product-finder/new/gender/diverse.png" },
  ]

  const styleOptionsMap = {
    woman: [
      { id: "timeless", label: "Timeless", image: "/iframe/product-finder/new/second-question/timeless.png" },
      { id: "unique", label: "Unique", image: "/iframe/product-finder/new/second-question/unique.png" },
      { id: "sporty", label: "Sporty", image: "/iframe/product-finder/new/second-question/sporty.png" },
      { id: "confident", label: "Confident", image: "/iframe/product-finder/new/second-question/confident.png" },
    ],
    man: [
      { id: "timeless", label: "Timeless", image: "/iframe/product-finder/new/second-question/timeless.png" },
      { id: "unique", label: "Unique", image: "/iframe/product-finder/new/second-question/unique.png" },
      { id: "sporty", label: "Sporty", image: "/iframe/product-finder/new/second-question/sporty.png" },
      { id: "confident", label: "Confident", image: "/iframe/product-finder/new/second-question/confident.png" },
    ],
    diverse: [
      { id: "timeless", label: "Timeless", image: "/iframe/product-finder/new/second-question/timeless.png" },
      { id: "unique", label: "Unique", image: "/iframe/product-finder/new/second-question/unique.png" },
      { id: "sporty", label: "Sporty", image: "/iframe/product-finder/new/second-question/sporty.png" },
      { id: "confident", label: "Confident", image: "/iframe/product-finder/new/second-question/confident.png" },
    ],
  }

  const handleTargetSelect = (targetId: string) => {
    setSelectedTarget(targetId)
    setStep(2)
  }

  const handleGenderSelect = (genderId: string) => {
    setSelectedGender(genderId)
    setStep(3)
  }

  const handleStyleSelect = (styleId: string) => {
    setSelectedStyle(styleId)
    setStep(4)
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1)
      if (step === 2) {
        setSelectedTarget(null)
      }
      if (step === 3) {
        setSelectedGender(null)
      }
      if (step === 4) {
        setSelectedStyle(null)
      }
    }
  }

  const getProgressPercentage = () => {
    return (step / 3) * 100
  }

  const getStyleOptions = () => {
    if (!selectedGender) return []
    return styleOptionsMap[selectedGender as keyof typeof styleOptionsMap]
  }

  const getResultImage = () => {
    if (!selectedGender || !selectedStyle) {
      return "/iframe/product-finder/new/product-suggestion/female_timeless.png"
    }

    // Map to gender-specific product suggestion images based on available files
    let imageName = "female_timeless.png"

    if (selectedGender === "woman") {
      const femaleMap: { [key: string]: string } = {
        timeless: "female_timeless.png",
        unique: "female_unique.png",
        sporty: "female_sporty.png",
        confident: "female_confident.png",
      }
      imageName = femaleMap[selectedStyle] || femaleMap.timeless
    } else if (selectedGender === "man") {
      const maleMap: { [key: string]: string } = {
        timeless: "male_timeless.png",
        unique: "male & diverse_unique.png",
        sporty: "male_sporty.png",
        confident: "male & diverse_confident.png",
      }
      imageName = maleMap[selectedStyle] || maleMap.timeless
    } else if (selectedGender === "diverse") {
      const diverseMap: { [key: string]: string } = {
        timeless: "male_timeless.png",
        unique: "male & diverse_unique.png",
        sporty: "male_sporty.png",
        confident: "male & diverse_confident.png",
      }
      imageName = diverseMap[selectedStyle] || diverseMap.timeless
    }

    return `/iframe/product-finder/new/product-suggestion/${imageName}`
  }

  return (
    <div>
      {isAutoMode && showCursor && (
        <div className={`floating-cursor animate`} />
      )}
      <div id="wrapper" className="max-w-sm relative mx-auto bg-black rounded-[3rem] p-2 h-[800px]">
        <div className="bg-white rounded-[2.5rem] overflow-hidden h-full relative flex flex-col">
          {/* Status Bar - Shared */}
          <div className="flex justify-between items-center px-6 py-2 text-black text-sm font-medium">
            <span>9:41</span>
            <div className="w-20 h-6 bg-black rounded-full"></div>
            <div className="flex items-center gap-1">
              <Signal className="w-4 h-4" />
              <Wifi className="w-4 h-4" />
              <Battery className="w-6 h-3" />
            </div>
          </div>

          {/* Email Header - Shared */}
          <div className="flex items-center justify-between px-4 py-2 border-b">
            {!isEmailClicked ? (
              // Initial view with Inboxes, Select, and dots
              <>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" className="text-blue-500 px-2 h-8">
                    Inboxes
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" className="text-blue-500 px-2 h-8">
                    Select
                  </Button>
                  <Button variant="ghost" className="text-blue-500 px-2 h-8">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                      <circle cx="5" cy="12" r="2" />
                      <circle cx="12" cy="12" r="2" />
                      <circle cx="19" cy="12" r="2" />
                    </svg>
                  </Button>
                </div>
              </>
            ) : (
              // Email detail view with back button and action buttons
              <>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    className="text-blue-500 px-2 h-8 flex items-center gap-1"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" className="text-blue-500 px-2 h-8">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                  <Button variant="ghost" className="text-blue-500 px-2 h-8 rotate-180">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Main Content Area with Sliding Animation */}
          <div className="main-content-wrapper">
            {/* Sliding Email List */}
            <div className={`sliding-content ${isEmailClicked ? 'animate' : ''}`}>
              {/* Title */}
              <h1 className="text-4xl font-bold px-4 pt-2 pb-0">All Mail</h1>

              {/* Search Bar */}
              <div className="px-4 py-2 border-b">
                <div className="bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search"
                    className="bg-transparent border-none outline-none text-sm w-full placeholder:text-gray-400"
                    readOnly
                  />
                </div>
              </div>

              <img src="/iframe/button-part.jpeg" alt="email-preview" className="w-full p-2" />

              {/* Product Finder Email */}
              <div className="relative">
                <div
                  className="email-preview cursor-pointer hover:bg-gray-50 relative transition-colors pulse-highlight"
                  style={{
                    paddingLeft: "25px"
                  }}
                  onClick={() => setIsEmailClicked(true)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="absolute left-[5px] top-[35px] transform translate-y-[-50%]">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  </div>
                  <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                    <Image src="/iframe/logo.png" alt="kinetic-logo" width={100} height={100} className="w-full h-full rounded-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-sm truncate">Jewellery Co.</h3>
                      <span className="text-xs text-gray-500 flex-shrink-0">8:43 AM</span>
                    </div>
                    <h4 className="font-medium text-sm text-gray-900">Find Your Perfect Jewellery</h4>
                    <p className="text-sm text-gray-500 truncate">
                      Hi Friend,<br/>
                      Not sure where to start? Try our product quiz to find your missing piece.
                    </p>
                  </div>
                </div>

                {!isAutoMode && (
                  <div className="click-hint-container">
                    <div className="click-indicator" />
                    <span className="click-hint-text" style={{
                      marginBottom: "-45px",
                      padding: "14px 20px",
                      marginLeft: "16px",
                      fontSize: "18px",
                      textAlign: "center",
                      display: "inline-block",
                      lineHeight: "1.4",
                      fontWeight: "700"
                    }}>Tap & try out <br />an interactive email</span>
                  </div>
                )}
              </div>

              {/* Other Emails */}
              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #1</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">8:15 AM</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Your Weekly Style Tips</h4>
                  <p className="text-sm text-gray-500 truncate">Discover the latest trends in jewellery and accessories...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #2</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">7:30 AM</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">New Arrivals</h4>
                  <p className="text-sm text-gray-500 truncate">Check out our latest collection of timeless pieces...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #3</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">Yesterday</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Exclusive Collection</h4>
                  <p className="text-sm text-gray-500 truncate">Be the first to explore our limited edition pieces crafted with rare gemstones...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #4</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">Yesterday</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Gift Guide</h4>
                  <p className="text-sm text-gray-500 truncate">Find the perfect gift for every occasion with our curated selection...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #5</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">2 days ago</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Care Tips</h4>
                  <p className="text-sm text-gray-500 truncate">Learn how to maintain and care for your precious jewellery pieces...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #6</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">3 days ago</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Behind the Scenes</h4>
                  <p className="text-sm text-gray-500 truncate">Discover the craftsmanship and artistry behind our signature designs...</p>
                </div>
              </div>
            </div>

            {/* Product Finder Content */}
            <div className="h-full overflow-y-auto">
              <div className="space-y-6 p-4">
                {/* Sender Info */}
                <div className="sticky top-0 z-10 -mt-4 -mx-4 px-4 pt-4 pb-3 bg-white border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center shadow-sm">
                        <Image src="/iframe/logo.png" alt="kinetic-logo" width={100} height={100} className="w-full h-full rounded-full" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">Jewellery Co.</div>
                        <div className="text-xs text-gray-500">To: info@kinetic.com</div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">8:43 AM</span>
                  </div>
                </div>

                {/* Hero Image */}
                <div className="w-full">
                  <img
                    src="/iframe/product-finder/hero.jpg"
                    alt="Jewellery Collection"
                    className="w-full h-auto object-cover"
                  />
                </div>

                {/* Email Content */}
                <div className="space-y-6">
                  {/* Header Text */}
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-3">
                      Find Your Perfect Jewellery
                    </h1>
                    <p className="text-gray-600 text-base">
                    Hi Friend,<br/>
                    Not sure where to start? Try our product quiz to find your missing piece.
                    </p>
                  </div>

                  {/* Progress Bar */}
                  {step <= 3 && (
                    <div className="mb-8">
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-black transition-all duration-500 ease-out"
                          style={{ width: `${getProgressPercentage()}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Question {step} of 3
                      </p>
                    </div>
                  )}

                  {/* Quiz Content */}
                  <div className="min-h-[400px]">
                    {/* Step 1: Target Selection */}
                    {step === 1 && (
                      <div className="space-y-6">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Who are you looking for?
                          </h2>
                        </div>
                        <div className="space-y-3">
                          {targetOptions.map((option) => (
                            <button
                              key={option.id}
                              onClick={() => handleTargetSelect(option.id)}
                              className="w-full text-center p-5 bg-gray-50 rounded-lg border-2 border-gray-300 hover:border-black hover:bg-white transition-all group"
                            >
                              <span className="text-gray-900 font-semibold text-base">
                                {option.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Step 2: Gender Selection */}
                    {step === 2 && (
                      <div className="space-y-6">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleBack}
                          className="mb-2 hover:bg-gray-100"
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Back
                        </Button>
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Who will be wearing the jewellery?
                          </h2>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {genderOptions.map((option) => (
                            <button
                              key={option.id}
                              onClick={() => handleGenderSelect(option.id)}
                              className="group relative overflow-hidden rounded-lg aspect-[2/3] transition-all hover:scale-[1.03] active:scale-[0.98] border-2 border-transparent hover:border-black shadow-md hover:shadow-xl"
                            >
                              <img
                                src={option.image}
                                alt={option.label}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                              <div className="absolute inset-0 flex items-end p-3">
                                <span className="text-white font-bold text-sm w-full text-center">
                                  {option.label}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Step 3: Style Selection */}
                    {step === 3 && (
                      <div className="space-y-6">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleBack}
                          className="mb-2 hover:bg-gray-100"
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Back
                        </Button>
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            What describes your / their style the most?
                          </h2>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {getStyleOptions().map((option) => (
                            <button
                              key={option.id}
                              onClick={() => handleStyleSelect(option.id)}
                              className="group relative overflow-hidden rounded-lg aspect-[2/3] transition-all hover:scale-[1.03] active:scale-[0.98] border-2 border-transparent hover:border-black shadow-md hover:shadow-xl"
                            >
                              <img
                                src={option.image}
                                alt={option.label}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                              <div className="absolute inset-0 flex items-end p-3">
                                <span className="text-white font-bold text-sm w-full text-center">
                                  {option.label}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Step 4: Results */}
                    {step === 4 && (
                      <div className="space-y-6">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            Perfect! Here's Your Match
                          </h2>
                          <p className="text-gray-600 text-base mb-6">
                            Based on your preferences, we've curated the perfect
                            jewellery selection for you.
                          </p>
                        </div>

                        {/* Result Image */}
                        <div className="mb-6 px-4">
                          <img
                            src={getResultImage()}
                            alt="Your jewellery recommendations"
                            className="w-full h-auto"
                          />
                        </div>

                        {/* CTA Button */}
                        <div>
                          <Button
                            className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-6 text-base"
                          >
                            Shop Your Recommendations
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Email Footer */}
                  <div className="px-4 py-8 bg-white border-t border-gray-200 mt-8">
                    {/* Follow Us Section */}
                    <div className="text-center mb-8">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4 tracking-wider">
                        FOLLOW US
                      </h3>
                      <div className="flex justify-center gap-6 mb-8">
                        <div className="text-gray-900">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                            <path d="M12 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.162 6.162 6.162 6.162-2.759 6.162-6.162c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                          </svg>
                        </div>
                        <div className="text-gray-900">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </div>
                        <div className="text-gray-900">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                          </svg>
                        </div>
                        <div className="text-gray-900">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 0c-6.627 0-12 5.372-12 12 0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146 1.124.347 2.317.535 3.554.535 6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
                          </svg>
                        </div>
                      </div>

                      {/* Features Section */}
                      <div className="border-t border-gray-200 pt-6">
                        <div className="grid grid-cols-2 gap-8">
                          <div className="text-center">
                            <div className="mb-2">
                              <svg className="w-8 h-8 mx-auto text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                            </div>
                            <p className="text-sm text-gray-900 font-medium">Lifetime Guarantee</p>
                          </div>
                          <div className="text-center">
                            <div className="mb-2">
                              <svg className="w-8 h-8 mx-auto text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                              </svg>
                            </div>
                            <p className="text-sm text-gray-900 font-medium">Free Returns</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Company Info */}
                    <div className="text-center pt-6 border-t border-gray-200">
                      <p className="text-xs text-gray-600 mb-2">
                        Jewellery Co. Ltd. Main Street 123, New York, NY 10001, USA
                      </p>
                      <p className="text-xs text-gray-500">
                        No longer want to receive these emails?{" "}
                        <span className="underline text-gray-700">
                          Unsubscribe
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Navigation - Shared */}
          <div className="flex justify-around py-4 border-t bg-gray-50 mt-auto">
            <Archive className="w-5 h-5 text-blue-500" />
            <Folder className="w-5 h-5 text-blue-500" />
            <ArrowLeft className="w-5 h-5 text-blue-500" />
            <SquarePen className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function IPhoneEmailProductFinder() {
  return (
    <Suspense>
      <IPhoneEmailProductFinderContent />
    </Suspense>
  )
}
